import { ref, useFetch } from '@nuxtjs/composition-api';
import { uniq } from 'lodash-es';
import { useStore } from '~/composables/useStore';
import { useWOVNio } from '~/composables/wovn/useWOVNio';
import { WINE_INTRODUCTION_REVIEW_THRESHOLD, PRODUCT_CART_BUTTON } from '~/constants';
import { url, getAromaImageUrl } from '~/utils';
import { getLotteryInfoFromEC } from '~/utils/checkLotteryStatus';
import {
  formatProducingArea,
  getSpecialPricePromotion,
  formatKeyValueArray,
  getProductPrice,
  parseReviewMediumInfo,
} from '~/utils/modules/products';
import { CommonProducingArea } from '~/types/common';
import { DynamicPartsWineIntroduction } from '~/types/modules/contents';
import { ProductStockCard, ProductShowECResponseSchema, ProductInfoForIntroParts } from '~/types/modules/product';
import { AromaType, DisplayLang } from '~/enums';

export const useWineIntroduction = (props: { item: DynamicPartsWineIntroduction; productCode: string }) => {
  const store = useStore();
  const productDetailUrl = url('ITEM_DETAIL', { code: props.productCode });
  const ecDetailResponse = ref<ProductShowECResponseSchema>();
  const searchProduct = ref<ProductInfoForIntroParts>();
  const { currentLang } = useWOVNio();

  /** 新着ラベル（NEW）を表示するかどうか */
  const isShowNew = ref<boolean>();
  /** 特集用キャッチ（管理画面の入力項目名） */
  const threeLineCatchCopies = ref<Array<string>>([]);
  /** 説明（管理画面の入力項目名） */
  const catchCopies = ref<Array<string>>([]);
  /** クチコミ（平均値とレビュー件数） */
  const review = ref<{ rating_average: number; review_total: number }>();
  /** 生産者名 */
  const producer = ref<{ name_kana: string; name: string; id: number }>();
  /** 商品名 */
  const productName = ref<string>();
  /** 管理画面側で指定された子商品があるかどうか */
  const hasProductVariants = ref<boolean>();
  /** ヴィンテージモーダルが有効かどうか */
  const isActiveModal = ref<boolean>();
  /**
   * 子商品のリスト
   * 管理画面で子商品コード未指定の場合、最安の子商品のみ取得
   * 子商品コード指定ありの場合、指定された子商品全件取得
   */
  const stockList = ref<Array<ProductStockCard>>([]);
  /** 生産地名 */
  const producingArea = ref<CommonProducingArea>();
  /** 評価誌 */
  const filteredReviews = ref<Array<{ name: string; score: number }>>([]);
  /** 子商品の在庫が1つでもあるかどうか */
  const hasStock = ref<boolean>();
  /** 子商品が1つでも販売可能かどうか */
  const isNowOnSale = ref<boolean>();

  useFetch(async () => {
    if (!props.productCode) return;

    // Propsから情報を取得
    threeLineCatchCopies.value = props.item.catch_text?.split('\n') || [];
    catchCopies.value = props.item.catch_copy?.split('\n') || [];
    hasProductVariants.value = !!props.item.product_variant_codes.length;

    // ECから情報を取得
    const res = await store.dispatch('product/detail/fetchECProductDetail', {
      path: {
        code: props.productCode,
      },
    });
    ecDetailResponse.value = res;
    if (!ecDetailResponse.value) return;

    const { best_priced_variant_id, master_settings, product_variants, review_settings } = ecDetailResponse.value;
    if (review_settings) {
      const { rating_average, review_total } = review_settings;
      review.value =
        rating_average >= WINE_INTRODUCTION_REVIEW_THRESHOLD ? { rating_average, review_total } : undefined;
    }
    if (master_settings.producer) {
      const { name_kana, name, id } = master_settings.producer;
      producer.value = { name_kana, name, id };
    }
    productName.value =
      currentLang.value.code === DisplayLang.JA
        ? ecDetailResponse.value.langs.find((lang) => lang.id === 'ja')?.name || ''
        : ecDetailResponse.value.langs.find((lang) => lang.id === 'en')?.name || '';
    isActiveModal.value = !hasProductVariants.value && product_variants.length >= 2;
    const productVariants = product_variants?.filter((variant) => {
      if (!hasProductVariants.value && variant.id === best_priced_variant_id) return variant;
      return variant.code && props.item.product_variant_codes?.includes(variant.code);
    });
    if (productVariants) {
      stockList.value = productVariants.map((variant): ProductStockCard => {
        const formattedAttributes = formatKeyValueArray(variant?.attributes);
        const SpecialPricePromotion = getSpecialPricePromotion(variant?.price_settings);
        return {
          productVariantId: Number(variant?.id),
          isFreeShipping: Boolean(variant?.price_settings?.is_free_shipping),
          discountRate: SpecialPricePromotion?.discount_rate,
          stock: variant?.stock_count || 0,
          saleLabel: variant?.price_settings?.sale_info,
          price: getProductPrice(variant?.price_settings),
          vintageCode: variant?.vintage_code,
          reviewMediumInfos: parseReviewMediumInfo(variant.review_medium_info),
          isLimitedStock: formattedAttributes?.mark_limited_stock_flag === 'true',
          isOnlineCampaign: formattedAttributes?.mark_limited_online_campaign_flag === 'true',
          isNowOnSale: variant.now_on_sale_flag,
          sameProductPromotionNames: uniq(variant.single_product_promotions.map(({ name }) => name) || []),
          multiProductPromotionNames: uniq(variant.multi_product_promotions.map(({ name }) => name) || []),
          brands: [],
          isD1Target: variant.d1_icon_display_flag,
          earliestShippingDate: variant.earliest_shipping_date,
          lottery: getLotteryInfoFromEC(variant.raffle_item?.raffle),
        };
      });
    }
    producingArea.value = formatProducingArea(master_settings?.producing_areas);
    hasStock.value = !!product_variants?.some((variant) => variant.stock_count > 0);
    isNowOnSale.value = !!product_variants?.some((variant) => variant.now_on_sale_flag);

    // サーチから情報を取得
    searchProduct.value = await store.dispatch('modules/product/fetchInfoForIntroductionParts', {
      q: `mst:product AND product_code_s: ${ecDetailResponse.value.code}`,
    });

    const { is_new, wa_point, we_point, ws_point, js_point, d_point, v_point, gr_point } = searchProduct.value;
    isShowNew.value = !!is_new;
    const obj = {
      wa_point,
      we_point,
      ws_point,
      js_point,
      d_point,
      v_point,
      gr_point,
    };
    const reviews = Object.entries(obj)
      .filter((item) => item[1] && typeof item[1] === 'number')
      .map((item) => {
        return {
          name: item[0].split('_')[0].toUpperCase(),
          score: Number(item[1]),
        };
      });
    filteredReviews.value = reviews.sort((a, b) => b.score - a.score).slice(0, 2);
  });

  return {
    url,
    producingArea,
    productDetailUrl,
    ecDetailResponse,
    filteredReviews,
    stockList,
    threeLineCatchCopies,
    catchCopies,
    hasStock,
    isNowOnSale,
    isShowNew,
    getAromaImageUrl,
    AromaType,
    producer,
    productName,
    isActiveModal,
    review,
    currentLang,
    DisplayLang,
    PRODUCT_CART_BUTTON,
  };
};
